import { api, LightningElement, wire } from "lwc";

import { logger } from "@devforce/trailhead-client-logger";

import {
  createConfig,
  CollectionChangeEvent,
  CollectionQuery,
  CollectionState,
  CollectionItem,
  CollectionConfig,
} from "common/contentCollection";
import type { ContentSummary } from "common/contentSummary";
import { EffectAdapter } from "common/effectAdapter";
import {
  QueryAdapter,
  QueryAdapterOptions,
  QueryAdapterValue,
} from "common/graphqlAdapter";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { memoizeDependencies } from "common/memoize";
import { trackUserActivity, trackUserActivityLearning } from "common/track";

import type { CtaEvent } from "tds/card";
import {
  Config as LoadingStateConfig,
  State as LoadingState,
} from "tds/contentCollectionLoadingState";
import { showToast } from "tds/toaster";

import * as locales from "./locales";

type QueryMode = "append" | null;

export default class extends LightningElement {
  @api config: CollectionConfig | null = null;
  @api query: CollectionQuery<unknown> | null = null;
  @api queryMode: QueryMode = null;
  @api pageLocation: string | null = null;

  private contentActionsContructor: typeof LightningElement | null = null;
  private hasMore: boolean | undefined = false;
  private items: CollectionItem[] | null = null;

  private queryError = false;
  private queryLoading = true;
  private queryOptionsCache: QueryAdapterOptions | undefined = undefined;
  private queryRefreshKey = 0;
  // TODO: investigate a different way to achieve this functionality
  private eventType = { cta: false };
  // Values that we don't want to trigger an LWC update
  private queryMeta: {
    state: CollectionState | undefined;
  } = {
    state: undefined,
  };

  private hasLoaded: boolean = false;
  private totalCount: number | undefined = 0;
  private lastCollectionItem: Element | undefined = undefined;

  renderedCallback() {
    if (this.hasLoaded) return;
    if (this.state === "loaded") {
      this.manageFocus();
    }
    if (!this.isNewRecommendationQuery()) return;

    if (this.state === "loaded" && this.items?.length) {
      const contentUids = this.items?.map((i) => i.summary.id);
      const recommendationAction = this.items[0].action!;

      if (
        recommendationAction.eventData?.name &&
        recommendationAction.eventData?.id
      ) {
        const { name: recommendationProvider, id: recommendationId } =
          recommendationAction.eventData;

        trackUserActivity(this, "recommendation_items_viewed", {
          contentUids,
          recommendationId,
          recommendationProvider,
        });

        this.hasLoaded = true;
      }
    }
  }

  private configFinalMemoized = memoizeDependencies(
    () => {
      return createConfigFinal(this.config, {
        hasMore: this.hasMore,
        items: this.items,
        queryMode: this.queryMode,
        state: this.state,
      });
    },
    () => [this.config, this.hasMore, this.items, this.queryMode, this.state]
  );

  private get configFinal(): CollectionConfig {
    return this.configFinalMemoized();
  }

  private get loadingConfig(): LoadingStateConfig {
    return {
      ...this.configFinal.state,
      state: this.state,
    };
  }

  private get queryOptions() {
    if (!this.query) return undefined;
    let metaState = this.eventType.cta ? this.queryMeta.state : undefined;

    try {
      return (
        this.query?.getQueryOptions?.(metaState) ??
        // @ts-ignore (depreacted, backwards-compatibility)
        this.query.queryOptions
      );
    } catch (e) {
      this.queryError = true;
      logger.error(e);
      return undefined;
    }
  }

  private get queryUseFetch() {
    return this.query?.useFetch === true;
  }

  private get showLoadingEmptyErrorState() {
    return !(this.queryMode && this.items && this.items.length > 0);
  }

  private get state(): LoadingState {
    if (this.queryError) return "error";
    if (this.queryLoading) return "loading";
    if (this.items && this.items.length) return "loaded";
    return "empty";
  }

  private manageFocus() {
    if (this.lastCollectionItem && this.lastCollectionItem.nextSibling) {
      const nextElement = this.lastCollectionItem.nextSibling as HTMLElement;
      this.lastCollectionItem = undefined;
      nextElement.setAttribute("tabindex", "-1");
      nextElement.focus();
    }
  }

  private setLastCollectionItemElement() {
    this.lastCollectionItem = Array.from(
      this.template.querySelectorAll('[data-name="content-collection-item"]')
    ).pop() as HTMLElement;
  }

  private handleCta(e: CtaEvent) {
    this.setLastCollectionItemElement();

    this.eventType.cta = true;

    if (this.queryMode) this.queryRefreshKey++;
    trackUserActivity(this, "cta_clicked", {
      ctaType: "primary",
      label: e.detail.ctaLabel ?? "",
      pageLocation: this.pageLocation ?? "th-content-collection",
    });
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private handleNavigate(e: CustomEvent<ContentSummary>) {
    const item = this.items?.find((i) => i.summary.id === e.detail.id);

    if (item && item.action && item.action.learning) {
      trackUserActivityLearning(this, item.action.learning, {
        pageLocation: this.pageLocation ?? "th-content-collection",
      });

      if (this.isRecommendation()) {
        this.trackRecommendationClicked(item);
      }
    } else {
      this.trackItemNavigation(e.detail);
    }

    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private trackItemNavigation(summary: ContentSummary) {
    trackUserActivity(this, "link_clicked", {
      label: summary.title ?? summary.id,
      href: summary.path,
      pageLocation: this.pageLocation ?? "th-content-collection",
      pathName: `${window.location.pathname}${window.location.search}`,
    });
  }

  // TODO: this should be handled in recommendation component by listening to an event
  // or passing a click handling function
  private trackRecommendationClicked(item: CollectionItem) {
    if (item.action?.eventData?.name && item.action.eventData?.id) {
      const {
        learning: { name: apiName, type },
        eventData: { name, id },
      } = item.action;
      let payload;

      if (!this.isNewRecommendationQuery()) {
        payload = {
          recommenderName: name,
          recommendationSetId: id,
        };
      } else {
        payload = {
          recommendationProvider: name,
          recommendationId: id,
        };
      }

      payload = {
        ...payload,
        type,
        apiName,
        contentUid: item.summary.id,
      };

      trackUserActivity(this, "recommendation_clicked", payload);
    }
  }

  // TODO: this should be handled in recommendation component by listening to an event
  // or passing a click handling function
  private isRecommendation() {
    if (this.queryOptions?.query.includes("getUserRecommendations")) {
      return true;
    }

    if (this.isNewRecommendationQuery()) {
      return true;
    }
  }

  // TODO: this should be handled in recommendation component by listening to an event
  // or passing a click handling function
  private isNewRecommendationQuery() {
    if (this.queryOptions?.query.includes("getUserLearningRecommendations")) {
      return true;
    }
  }

  // TODO: this should be handled in recommendation component by listening to an event
  // or passing a click handling function
  private handleDynamicCtaClick(e: CtaEvent) {
    trackUserActivity(this, "cta_clicked", {
      ctaType: "dynamic",
      ctaHref: e.detail.ctaHref ?? "",
      label: e.detail.ctaLabel ?? "",
      pageLocation: this.pageLocation ?? "th-content-collection",
    });
    this.dispatchEvent(
      new CustomEvent(e.type, { detail: e.detail, bubbles: true })
    );
  }

  private handleRefresh(e: CustomEvent<unknown>) {
    this.queryRefreshKey++;
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  private get labelOptions() {
    return {
      ariaTotalResultsLoaded: {
        totalResults: this.totalCount,
      },
    };
  }

  @wire(EffectAdapter, {
    options: "$queryOptions",
    refreshKey: "$queryRefreshKey",
    useFetch: "$queryUseFetch",
  })
  private onBeforeQuery() {
    this.queryError = false;
    this.queryLoading = true;
  }

  @wire(QueryAdapter, {
    options: "$queryOptions",
    refreshKey: "$queryRefreshKey",
    useFetch: "$queryUseFetch",
  })
  private onResult(result: QueryAdapterValue<unknown>) {
    try {
      let { items, query, queryMode } = this;
      if (!query) throw new Error("NO_QUERY");
      let nextItems = query.getCollectionItems(result);
      if (query.getTotalItemCount) {
        this.totalCount = query.getTotalItemCount(result);
        this.dispatchEvent(
          new CustomEvent("count", { detail: this.totalCount })
        );
      }
      this.items =
        queryMode === "append" && items && this.eventType.cta
          ? mergeItems(items, nextItems)
          : nextItems;
      this.hasMore = query.hasMore?.(result);
      this.queryMeta.state = query.getState?.(result);
    } catch (e) {
      logger.error(e);
      // If items have already been loaded, show a toast
      if (this.queryMode && this.items) {
        showToast({ message: this.labels.lazyLoadError, variant: "error" });
      } else {
        this.queryError = true;
      }
    } finally {
      this.queryLoading = false;
      this.eventType.cta = false;
    }
  }

  @wire(EffectAdapter, { config: "$config" })
  private async onConfigChange() {
    if (this.contentActionsContructor) return;
    if (this.config?.item?.hideAction) return;
    import("th/contentActions")
      .then((m) => {
        this.contentActionsContructor = m.default;
      })
      .catch((e) => logger.error(e));
  }

  @wire(EffectAdapter, { items: "$items", error: "$queryError" })
  private onStateChange() {
    let { state } = this;
    let event: CollectionChangeEvent = new CustomEvent("statechange", {
      detail: {
        state,
        items: this.items?.map((item) => item.summary),
      },
    });
    this.dispatchEvent(event);
  }

  @wire(EffectAdapter, { query: "$query" })
  private onQuery() {
    let prev = this.queryOptionsCache;
    let next = this.queryOptions;
    this.queryOptionsCache = next;
    if (!prev || !next) return;
    let { query: prevQuery, ...prevOptions } = prev;
    let { query: nextQuery, ...nextOptions } = next;
    if (
      prevQuery === nextQuery &&
      JSON.stringify(prevOptions) === JSON.stringify(nextOptions)
    ) {
      return;
    }
    this.queryRefreshKey++;

    const { tab: prevTab, filter: prevFilters } = prev.variables ?? {};
    const { tab: nextTab, filter: nextFilters } = next.variables ?? {};

    if (
      (prevTab && nextTab && prevTab !== nextTab) ||
      (prevFilters && nextFilters && prevFilters !== nextFilters)
    ) {
      this.items = null;
    }
  }
}

export function mergeItems(prev: CollectionItem[], next: CollectionItem[]) {
  return prev
    .filter((p) => !next.find((n) => n.summary.id === p.summary.id))
    .concat(next);
}

export function createConfigFinal(
  initialConfig: CollectionConfig | null,
  context: {
    hasMore?: boolean;
    items: CollectionItem[] | null;
    queryMode: QueryMode;
    state: LoadingState;
  }
) {
  let config = createConfig(initialConfig);
  if (config.card) {
    let showCta =
      config.card.ctaAlways === true ||
      (context.state === "loaded" && context.hasMore) ||
      (context.state === "loading" && context.hasMore && context.queryMode);
    if (!showCta) {
      delete config.card.ctaLabel;
      delete config.card.ctaHref;
    }
    if (context.queryMode) {
      delete config.card.ctaHref;
    }
    if (context.queryMode && context.items && context.state === "loading") {
      config.card.ctaDisabled = true;
      config.card.ctaSpinner = true;
    }
    // Prevent the loading state from displaying in a grid
    if (
      context.state !== "loaded" &&
      !(context.state === "loading" && config.state?.loading?.preserveLayout)
    ) {
      config.card.layout = "stack";
    }
  }
  return config;
}
